import React, { useState, useEffect, useRef } from 'react';
import { annuitySchemeList, annuityFrequencyList } from './data';
import CustomDropdown from './CustomDropdown';
import AnnuitySchemeDropdown from './AnnuitySchemeDropdown';
import CustomDatePicker from './CustomDatePicker';
import { getBankPensionData, IMAGE_URL } from '../../../Api/commonAPI';
import { decryptData } from '../../../Decrypt/Decrypt';
import {
  validatePRCorpusLumpsum,
  validateNPSCorpus,
  validateWithdrawalPR,
  validateWithdrawalAmount,
  validateAnnuityValue,
  validateWorkSectorDOB,
  validateRequiredField,
  checkSectionCValidity,
  calculateAge
} from './ValidationUtils';

const SectionC = ({ formData: parentFormData, onDataChange, onValidationChange, dobPran }) => {
  // Helper function to get current date in DD-MM-YYYY format
  const getCurrentDate = () => {
    const today = new Date();
    const day = String(today.getDate()).padStart(2, '0');
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const year = today.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Helper function to format number with Indian numbering system
  // Examples: 1000 → "1,000" | 50000 → "50,000" | 100000 → "1,00,000" | 1000000 → "10,00,000"
  const formatIndianNumber = (value) => {
    // Handle null, undefined, empty string, or 0
    if (value === null || value === undefined || value === '' || value === 0) {
      return '';
    }

    // Convert to number first to handle .00 cases
    const numValue = parseFloat(value);
    if (isNaN(numValue)) {
      return '';
    }

    // If it's a whole number, format without decimals
    const isWholeNumber = numValue % 1 === 0;
    const strValue = isWholeNumber ? numValue.toString() : numValue.toFixed(2);

    if (strValue === '' || strValue === '0') {
      return '';
    }

    // Check if negative
    const isNegative = strValue.startsWith('-');
    const cleanValue = isNegative ? strValue.substring(1) : strValue;

    // Split by decimal point if exists
    const parts = cleanValue.split('.');
    const integerPart = parts[0];
    let decimalPart = parts.length > 1 ? '.' + parts[1] : '';

    // Remove trailing zeros from decimal part
    if (decimalPart) {
      decimalPart = decimalPart.replace(/0+$/, '');
      if (decimalPart === '.') {
        decimalPart = '';
      }
    }

    // Indian numbering system: first 3 digits from right, then groups of 2
    let formattedInteger = '';
    const length = integerPart.length;

    if (length <= 3) {
      // No comma needed for numbers <= 999
      formattedInteger = integerPart;
    } else {
      // Last 3 digits (no comma)
      const lastThree = integerPart.substring(length - 3);

      // Remaining digits (groups of 2)
      const remaining = integerPart.substring(0, length - 3);
      const remainingFormatted = remaining.replace(/\B(?=(\d{2})+(?!\d))/g, ',');

      formattedInteger = remainingFormatted + ',' + lastThree;
    }

    // Add negative sign if needed
    return (isNegative ? '-' : '') + formattedInteger + decimalPart;
  };

  // Helper function to format number with commas (for backward compatibility)
  const formatNumberWithCommas = (value) => {
    return formatIndianNumber(value);
  };

  // Helper function to format number without unnecessary .00
  const formatNumber = (value) => {
    if (!value && value !== 0) return '0';
    const num = parseFloat(value);
    if (isNaN(num)) return '0';
    // If it's a whole number, return without decimals, otherwise return with 2 decimals
    return num % 1 === 0 ? num.toString() : num.toFixed(2);
  };

  // Helper function to remove commas and get numeric value
  const removeCommas = (value) => {
    if (!value) return '';
    return value.toString().replace(/,/g, '');
  };

  // Helper function to convert ISO date format (YYYY-MM-DDTHH:mm:ss or YYYY-MM-DD) to DD-MM-YYYY
  const convertISOToDDMMYYYY = (dateString) => {
    if (!dateString) return '';

    try {
      // Handle ISO format: "2025-12-11T00:00:00" or "2025-12-11"
      let datePart = dateString;
      if (dateString.includes('T')) {
        datePart = dateString.split('T')[0];
      }

      // Check if already in DD-MM-YYYY format
      const ddmmyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
      if (ddmmyyyyPattern.test(datePart)) {
        return datePart; // Already in correct format
      }

      // Convert YYYY-MM-DD to DD-MM-YYYY
      const yyyymmddPattern = /^(\d{4})-(\d{2})-(\d{2})$/;
      if (yyyymmddPattern.test(datePart)) {
        const parts = datePart.split('-');
        return `${parts[2]}-${parts[1]}-${parts[0]}`;
      }

      return dateString; // Return as-is if format is unrecognized
    } catch (error) {
      console.error('Error converting date format:', error);
      return '';
    }
  };

  // Helper function to convert DD-MM-YYYY to YYYY-MM-DD for validation
  const convertDDMMYYYYToYYYYMMDD = (ddmmyyyy) => {
    if (!ddmmyyyy) return '';
    const parts = ddmmyyyy.split('-');
    if (parts.length === 3) {
      return `${parts[2]}-${parts[1]}-${parts[0]}`; // Convert to YYYY-MM-DD
    }
    return '';
  };

  // Helper function to convert MM-DD-YYYY (from CustomDatePicker) to DD-MM-YYYY (internal format)
  const convertMMDDYYYYToDDMMYYYY = (mmddyyyy) => {
    if (!mmddyyyy) return '';
    const mmddyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
    if (mmddyyyyPattern.test(mmddyyyy)) {
      const parts = mmddyyyy.split('-');
      return `${parts[1]}-${parts[0]}-${parts[2]}`; // Swap month and day
    }
    return mmddyyyy;
  };

  // Helper function to convert DD-MM-YYYY (internal format) to MM-DD-YYYY (for CustomDatePicker)
  const convertDDMMYYYYToMMDDYYYY = (ddmmyyyy) => {
    if (!ddmmyyyy) return '';
    const ddmmyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
    if (ddmmyyyyPattern.test(ddmmyyyy)) {
      const parts = ddmmyyyy.split('-');
      return `${parts[1]}-${parts[0]}-${parts[2]}`; // Swap month and day
    }
    // If already in MM-DD-YYYY format, return as is
    const mmddyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
    if (mmddyyyyPattern.test(ddmmyyyy)) {
      return ddmmyyyy;
    }
    // If in YYYY-MM-DD format, convert to MM-DD-YYYY
    const yyyymmddPattern = /^(\d{4})-(\d{2})-(\d{2})$/;
    if (yyyymmddPattern.test(ddmmyyyy)) {
      const parts = ddmmyyyy.split('-');
      return `${parts[1]}-${parts[2]}-${parts[0]}`;
    }
    return ddmmyyyy;
  };

  const defaultFormData = {
    normalWithdrawal: 'yes',
    PRCorpusLumpsum: '',
    annuityPercentage: '',
    WorkSectorName: '',
    WorkSectorDOB: getCurrentDate(),
    NPSCorpus: '',
    WithdrawalPR: '',
    WithdrawalAmount: '',
    AnnuityValue: '',
    AnnuitySchemeName: '',
    AnnuityFrequencySchemeName: ''
  };

  // Initialize with parentFormData if available, otherwise use default
  let initialFormData;
  if (parentFormData && parentFormData.PRCorpusLumpsum) {
    // Has backend data - use it
    const processedParentData = { ...parentFormData };
    // Convert WorkSectorDOB from ISO format to DD-MM-YYYY if needed
    if (processedParentData.WorkSectorDOB) {
      processedParentData.WorkSectorDOB = convertISOToDDMMYYYY(processedParentData.WorkSectorDOB);
    }
    // Remove commas from NPSCorpus if present (store numeric value only)
    if (processedParentData.NPSCorpus) {
      processedParentData.NPSCorpus = removeCommas(processedParentData.NPSCorpus);
    }
    initialFormData = { ...defaultFormData, ...processedParentData };
  } else {
    // No backend data - use default, but if DOBPran is available, use it for WorkSectorDOB
    initialFormData = { ...defaultFormData };
    if (dobPran) {
      const convertedDOBPran = convertISOToDDMMYYYY(dobPran);
      if (convertedDOBPran) {
        initialFormData.WorkSectorDOB = convertedDOBPran;
      }
    }
  }

  const [formData, setFormData] = useState(initialFormData);

  // Refs to prevent circular updates
  const lastParentDataRef = useRef(JSON.stringify(parentFormData || {}));
  const isUpdatingFromParentRef = useRef(false);
  const onDataChangeRef = useRef(onDataChange);

  // Update ref when onDataChange changes
  useEffect(() => {
    onDataChangeRef.current = onDataChange;
  }, [onDataChange]);

  const [currentAge, setCurrentAge] = useState(() => {
    // Calculate initial age from WorkSectorDOB using imported calculateAge
    if (initialFormData.WorkSectorDOB) {
      const dateForCalculation = convertDDMMYYYYToYYYYMMDD(initialFormData.WorkSectorDOB) || initialFormData.WorkSectorDOB;
      return calculateAge(dateForCalculation);
    }
    const currentDateForCalc = convertDDMMYYYYToYYYYMMDD(getCurrentDate()) || getCurrentDate();
    return calculateAge(currentDateForCalc);
  });
  const [bankList, setBankList] = useState([]);
  const [selectedBanks, setSelectedBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);

  // Validation errors state
  const [errors, setErrors] = useState({
    PRCorpusLumpsum: null,
    WorkSectorName: null,
    AnnuitySchemeName: null,
    AnnuityFrequencySchemeName: null,
    WorkSectorDOB: null,
    NPSCorpus: null,
    WithdrawalPR: null,
    WithdrawalAmount: null,
    AnnuityValue: null
  });

  const workSectorList = [
    'Central Goverment',
    'State Government',
    'Private Sector',
    'Public Sector'
  ];

  // Update WorkSectorDOB from DOBPran when there's no backend data
  useEffect(() => {
    // Only update if there's no backend data (no PRCorpusLumpsum means no backend data)
    const hasBackendData = parentFormData && parentFormData.PRCorpusLumpsum && parentFormData.PRCorpusLumpsum !== '';
    if (!hasBackendData && dobPran) {
      const convertedDOBPran = convertISOToDDMMYYYY(dobPran);
      if (convertedDOBPran && formData.WorkSectorDOB !== convertedDOBPran) {
        // Only update if WorkSectorDOB is empty or is the default current date
        const today = getCurrentDate();
        const isDefaultDate = !formData.WorkSectorDOB || formData.WorkSectorDOB === today;
        if (isDefaultDate) {
          setFormData(prev => ({
            ...prev,
            WorkSectorDOB: convertedDOBPran
          }));
          // Calculate age
          const dateForCalculation = convertDDMMYYYYToYYYYMMDD(convertedDOBPran) || convertedDOBPran;
          const age = calculateAge(dateForCalculation);
          setCurrentAge(age);
        }
      }
    }
  }, [dobPran, parentFormData?.PRCorpusLumpsum]);

  // Update local formData when parentFormData changes (from API response)
  useEffect(() => {
    if (!parentFormData) return;

    const parentDataStr = JSON.stringify(parentFormData);

    // Only update if parent data is different and we're not currently updating from parent
    if (parentDataStr !== lastParentDataRef.current && !isUpdatingFromParentRef.current) {
      // Check if parent has new data (like from API)
      const hasNewData = (parentFormData.PRCorpusLumpsum && parentFormData.PRCorpusLumpsum !== formData.PRCorpusLumpsum) ||
        (parentFormData.WorkSectorName && parentFormData.WorkSectorName !== formData.WorkSectorName) ||
        (parentFormData.NPSCorpus && parentFormData.NPSCorpus !== formData.NPSCorpus) ||
        (parentFormData.WorkSectorDOB && parentFormData.WorkSectorDOB !== formData.WorkSectorDOB);

      if (hasNewData) {
        isUpdatingFromParentRef.current = true;
        const processedParentData = { ...parentFormData };
        // Convert WorkSectorDOB from ISO format to DD-MM-YYYY if needed
        if (processedParentData.WorkSectorDOB) {
          processedParentData.WorkSectorDOB = convertISOToDDMMYYYY(processedParentData.WorkSectorDOB);
        }
        // Remove commas from NPSCorpus if present (store numeric value only)
        if (processedParentData.NPSCorpus) {
          processedParentData.NPSCorpus = removeCommas(processedParentData.NPSCorpus);
        }

        const updatedFormData = {
          ...formData,
          ...processedParentData
        };

        setFormData(updatedFormData);
        lastParentDataRef.current = parentDataStr;

        // Calculate age if WorkSectorDOB is provided
        if (updatedFormData.WorkSectorDOB) {
          const dateForCalculation = convertDDMMYYYYToYYYYMMDD(updatedFormData.WorkSectorDOB) || updatedFormData.WorkSectorDOB;
          const age = calculateAge(dateForCalculation);
          setCurrentAge(age);
        }

        // Validate all fields after auto-fill
        const prCorpusLumpsumValue = parseFloat(updatedFormData.PRCorpusLumpsum) || 0;
        const isZeroWithdrawal = prCorpusLumpsumValue === 0;

        const newErrors = {
          PRCorpusLumpsum: validatePRCorpusLumpsum(updatedFormData.PRCorpusLumpsum || ''),
          WorkSectorName: validateRequiredField(updatedFormData.WorkSectorName || '', 'Work Sector'),
          AnnuitySchemeName: validateRequiredField(updatedFormData.AnnuitySchemeName || '', 'Annuity Scheme'),
          AnnuityFrequencySchemeName: validateRequiredField(updatedFormData.AnnuityFrequencySchemeName || '', 'Annuity Frequency'),
          WorkSectorDOB: (() => {
            const dateForValidation = convertDDMMYYYYToMMDDYYYY(updatedFormData.WorkSectorDOB || '') || updatedFormData.WorkSectorDOB || '';
            return validateWorkSectorDOB(dateForValidation);
          })(),
          NPSCorpus: validateNPSCorpus(updatedFormData.NPSCorpus || ''),
          WithdrawalPR: isZeroWithdrawal ? null : validateWithdrawalPR(updatedFormData.WithdrawalPR || ''),
          WithdrawalAmount: isZeroWithdrawal ? null : validateWithdrawalAmount(updatedFormData.WithdrawalAmount || ''),
          AnnuityValue: isZeroWithdrawal ? null : validateAnnuityValue(updatedFormData.AnnuityValue || '')
        };
        setErrors(newErrors);

        setTimeout(() => {
          isUpdatingFromParentRef.current = false;
        }, 100);
      }
    }
  }, [parentFormData]);

  // Initial validation on mount if formData has values
  useEffect(() => {
    // Calculate age from WorkSectorDOB on mount
    if (formData.WorkSectorDOB) {
      const dateForCalculation = convertDDMMYYYYToYYYYMMDD(formData.WorkSectorDOB) || formData.WorkSectorDOB;
      const age = calculateAge(dateForCalculation);
      setCurrentAge(age);
    }

    if (formData.PRCorpusLumpsum || formData.WorkSectorName || formData.NPSCorpus) {
      const prCorpusLumpsumValue = parseFloat(formData.PRCorpusLumpsum) || 0;
      const isZeroWithdrawal = prCorpusLumpsumValue === 0;

      const initialErrors = {
        PRCorpusLumpsum: validatePRCorpusLumpsum(formData.PRCorpusLumpsum || ''),
        WorkSectorName: validateRequiredField(formData.WorkSectorName || '', 'Work Sector'),
        AnnuitySchemeName: validateRequiredField(formData.AnnuitySchemeName || '', 'Annuity Scheme'),
        AnnuityFrequencySchemeName: validateRequiredField(formData.AnnuityFrequencySchemeName || '', 'Annuity Frequency'),
        WorkSectorDOB: (() => {
          const dateForValidation = convertDDMMYYYYToMMDDYYYY(formData.WorkSectorDOB || '') || formData.WorkSectorDOB || '';
          return validateWorkSectorDOB(dateForValidation);
        })(),
        NPSCorpus: validateNPSCorpus(formData.NPSCorpus || ''),
        WithdrawalPR: isZeroWithdrawal ? null : validateWithdrawalPR(formData.WithdrawalPR || ''),
        WithdrawalAmount: isZeroWithdrawal ? null : validateWithdrawalAmount(formData.WithdrawalAmount || ''),
        AnnuityValue: isZeroWithdrawal ? null : validateAnnuityValue(formData.AnnuityValue || '')
      };
      setErrors(prev => ({ ...prev, ...initialErrors }));
    }
  }, []); // Run only on mount

  // Pass data to parent component whenever it changes (but not when updating from parent)
  useEffect(() => {
    if (onDataChangeRef.current && !isUpdatingFromParentRef.current) {
      onDataChangeRef.current(formData);
    }
  }, [formData]); // Removed onDataChange from dependencies, using ref instead

  useEffect(() => {
    // Calculate withdrawal amount and annuity value when NPSCorpus and WithdrawalPR change
    if (formData.NPSCorpus && formData.WithdrawalPR) {
      const corpus = parseFloat(formData.NPSCorpus) || 0;
      const withdrawalPercent = parseFloat(formData.WithdrawalPR) || 0;
      const withdrawalAmount = (corpus * withdrawalPercent) / 100;
      const annuityValue = corpus - withdrawalAmount;

      setFormData(prev => ({
        ...prev,
        WithdrawalAmount: withdrawalAmount.toFixed(2),
        AnnuityValue: annuityValue.toFixed(2)
      }));

      // Special handling for 0: no validation errors when withdrawal is 0
      if (withdrawalPercent === 0) {
        setErrors(prev => ({
          ...prev,
          WithdrawalAmount: null, // No error when withdrawal is 0
          AnnuityValue: null // No error when withdrawal is 0
        }));
      } else {
        // Validate calculated fields for non-zero values
        setErrors(prev => ({
          ...prev,
          WithdrawalAmount: validateWithdrawalAmount(withdrawalAmount.toFixed(2)),
          AnnuityValue: validateAnnuityValue(annuityValue.toFixed(2))
        }));
      }
    } else if (formData.NPSCorpus && !formData.WithdrawalPR) {
      // If WithdrawalPR is empty, clear calculated fields and validate
      setFormData(prev => ({
        ...prev,
        WithdrawalAmount: '',
        AnnuityValue: ''
      }));

      setErrors(prev => ({
        ...prev,
        WithdrawalAmount: validateWithdrawalAmount(''),
        AnnuityValue: validateAnnuityValue('')
      }));
    } else if (!formData.NPSCorpus || !formData.WithdrawalPR) {
      // If either field is empty, clear calculated fields
      setFormData(prev => ({
        ...prev,
        WithdrawalAmount: '',
        AnnuityValue: ''
      }));

      setErrors(prev => ({
        ...prev,
        WithdrawalAmount: null,
        AnnuityValue: null
      }));
    }
  }, [formData.NPSCorpus, formData.WithdrawalPR]);

  // Auto-calculate annuityPercentage when PRCorpusLumpsum or WithdrawalPR changes
  useEffect(() => {
    // If both PRCorpusLumpsum and WithdrawalPR have the same value, calculate annuityPercentage
    if (formData.PRCorpusLumpsum && formData.WithdrawalPR) {
      const prValue = parseFloat(formData.PRCorpusLumpsum);
      const withdrawalValue = parseFloat(formData.WithdrawalPR);

      // Check if both values are same and valid
      if (!isNaN(prValue) && !isNaN(withdrawalValue) && prValue === withdrawalValue && prValue >= 0 && prValue <= 20) {
        const annuityPercent = Math.round(100 - prValue); // Round to whole number
        const calculatedAnnuity = annuityPercent.toString(); // No decimals

        // Only update if different to prevent infinite loops
        if (formData.annuityPercentage !== calculatedAnnuity) {
          setFormData(prev => ({
            ...prev,
            annuityPercentage: calculatedAnnuity
          }));
        }
      }
    } else if (!formData.PRCorpusLumpsum && !formData.WithdrawalPR) {
      // If both are empty, clear annuityPercentage
      if (formData.annuityPercentage !== '') {
        setFormData(prev => ({
          ...prev,
          annuityPercentage: ''
        }));
      }
    }
  }, [formData.PRCorpusLumpsum, formData.WithdrawalPR]);

  // Fetch bank pension data when required fields change
  useEffect(() => {
    const fetchBankData = async () => {
      // Only fetch if all required fields are filled
      if (!currentAge || !formData.AnnuityValue || !formData.AnnuityFrequencySchemeName || !formData.AnnuitySchemeName) {
        setBankList([]);
        // Clear selected banks when bank list is cleared
        setSelectedBanks([]);
        // Notify parent that no bank is selected
        if (onDataChange) {
          onDataChange({
            selectedBank: null,
            AnnuityServiceProviderName: '',
            annuityScheme: formData.AnnuitySchemeName,
            annuityFrequency: formData.AnnuityFrequencySchemeName
          });
        }
        return;
      }

      setLoadingBanks(true);
      try {
        const payload = {
          Age: currentAge,
          Amount: parseFloat(formData.AnnuityValue) || 0,
          AnnuityFrequency: formData.AnnuityFrequencySchemeName,
          AnnuityScheme: formData.AnnuitySchemeName,
          AnnuityValue: formData.AnnuityValue
        };

        const response = await getBankPensionData(payload);

        // Decrypt the response data
        if (response?.data?.data) {
          try {
            const decryptedData = decryptData(response.data.data);

            if (Array.isArray(decryptedData)) {
              setBankList(decryptedData);
            } else if (decryptedData && typeof decryptedData === 'object') {
              if (Array.isArray(decryptedData.data)) {
                setBankList(decryptedData.data);
              } else if (Array.isArray(decryptedData.BankList)) {
                setBankList(decryptedData.BankList);
              } else {
                setBankList([]);
              }
            } else {
              setBankList([]);
            }
          } catch (decryptError) {
            console.error('Error decrypting bank data:', decryptError);
            setBankList([]);
          }
        } else {
          setBankList([]);
        }
      } catch (error) {
        setBankList([]);
      } finally {
        setLoadingBanks(false);
      }
    };

    fetchBankData();
  }, [currentAge, formData.AnnuityValue, formData.AnnuityFrequencySchemeName, formData.AnnuitySchemeName]);

  // Pass validation status to parent (including bank selection check)
  useEffect(() => {
    if (onValidationChange) {
      const isFormValid = checkSectionCValidity(formData, errors);
      // Also check if bank is selected
      const isBankSelected = selectedBanks.length > 0;
      // Section C is valid only if form is valid AND bank is selected
      onValidationChange(isFormValid && isBankSelected);
    }
  }, [formData, errors, selectedBanks, onValidationChange]);

  // Sync selectedBanks state with parent data and notify parent if no bank is selected
  useEffect(() => {
    // If selectedBanks is empty, ensure parent knows no bank is selected
    if (selectedBanks.length === 0 && onDataChangeRef.current) {
      // Check if we need to notify parent (only if bankList is loaded)
      if (bankList.length > 0) {
        onDataChangeRef.current({
          selectedBank: null,
          AnnuityServiceProviderName: '',
          annuityScheme: formData.AnnuitySchemeName,
          annuityFrequency: formData.AnnuityFrequencySchemeName
        });
      }
    }
  }, [selectedBanks, bankList, formData.AnnuitySchemeName, formData.AnnuityFrequencySchemeName]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    let finalValue = value;

    // Validate PRCorpusLumpsum - max 20, allow empty, allow intermediate typing
    // Also sync with WithdrawalPR field
    if (name === 'PRCorpusLumpsum') {
      if (value === '' || value === null) {
        finalValue = '';
        // When empty, set annuityPercentage to empty and sync WithdrawalPR
        setFormData(prev => ({
          ...prev,
          [name]: '',
          WithdrawalPR: '',
          annuityPercentage: ''
        }));
        // Validate empty value
        const error = validatePRCorpusLumpsum('');
        setErrors(prev => ({
          ...prev,
          [name]: error,
          WithdrawalPR: validateWithdrawalPR('')
        }));
        return;
      } else {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          // Prevent values greater than 20 from updating state
          if (numValue > 20) {
            // Show error but don't update state
            const error = validatePRCorpusLumpsum(value);
            setErrors(prev => ({
              ...prev,
              [name]: error
            }));
            return; // Don't update formData
          }

          // Allow values between 0 and 20 - update state for all valid values
          // This allows typing "2" when user wants to type "20"
          finalValue = value;

          // Calculate annuityPercentage = 100 - PRCorpusLumpsum (only if valid)
          // Also sync the same value to WithdrawalPR
          if (numValue >= 0 && numValue <= 20) {
            const annuityPercent = Math.round(100 - numValue); // Round to whole number
            setFormData(prev => ({
              ...prev,
              [name]: finalValue,
              WithdrawalPR: finalValue, // Sync with WithdrawalPR
              annuityPercentage: annuityPercent.toString() // No decimals
            }));

            // Special handling for 0: no validation errors for WithdrawalPR, WithdrawalAmount, AnnuityValue
            if (numValue === 0) {
              setErrors(prev => ({
                ...prev,
                [name]: null, // No error for 0
                WithdrawalPR: null, // No error for 0
                WithdrawalAmount: null, // No error when withdrawal is 0
                AnnuityValue: null // No error when withdrawal is 0
              }));
            } else {
              // Validate WithdrawalPR as well for non-zero values
              const withdrawalError = validateWithdrawalPR(finalValue);
              setErrors(prev => ({
                ...prev,
                [name]: validatePRCorpusLumpsum(finalValue),
                WithdrawalPR: withdrawalError
              }));
            }
          } else {
            // Still update the field but don't calculate annuity
            setFormData(prev => ({
              ...prev,
              [name]: finalValue,
              WithdrawalPR: finalValue, // Sync with WithdrawalPR
              annuityPercentage: ''
            }));
            // Validate both fields
            setErrors(prev => ({
              ...prev,
              [name]: validatePRCorpusLumpsum(finalValue),
              WithdrawalPR: validateWithdrawalPR(finalValue)
            }));
          }
          return;
        }
      }
    }

    // Validate WithdrawalPR - max 20, also sync with PRCorpusLumpsum
    if (name === 'WithdrawalPR') {
      if (value === '' || value === null) {
        finalValue = '';
        // When empty, also clear PRCorpusLumpsum
        setFormData(prev => ({
          ...prev,
          [name]: '',
          PRCorpusLumpsum: '',
          annuityPercentage: ''
        }));
        // Validate both fields
        setErrors(prev => ({
          ...prev,
          [name]: validateWithdrawalPR(''),
          PRCorpusLumpsum: validatePRCorpusLumpsum('')
        }));
        return;
      } else {
        const numValue = parseFloat(value);
        if (!isNaN(numValue)) {
          // Prevent values greater than 20 from updating state
          if (numValue > 20) {
            // Show error but don't update state
            const error = validateWithdrawalPR(value);
            setErrors(prev => ({
              ...prev,
              [name]: error
            }));
            return; // Don't update formData
          }

          // Allow values between 0 and 20
          finalValue = value;

          // Sync the same value to PRCorpusLumpsum
          // Calculate annuityPercentage = 100 - PRCorpusLumpsum (only if valid)
          if (numValue >= 0 && numValue <= 20) {
            const annuityPercent = Math.round(100 - numValue); // Round to whole number
            setFormData(prev => ({
              ...prev,
              [name]: finalValue,
              PRCorpusLumpsum: finalValue, // Sync with PRCorpusLumpsum
              annuityPercentage: annuityPercent.toString() // No decimals
            }));

            // Special handling for 0: no validation errors for WithdrawalPR, WithdrawalAmount, AnnuityValue
            if (numValue === 0) {
              setErrors(prev => ({
                ...prev,
                [name]: null, // No error for 0
                PRCorpusLumpsum: null, // No error for 0
                WithdrawalAmount: null, // No error when withdrawal is 0
                AnnuityValue: null // No error when withdrawal is 0
              }));
            } else {
              // Validate both fields for non-zero values
              setErrors(prev => ({
                ...prev,
                [name]: validateWithdrawalPR(finalValue),
                PRCorpusLumpsum: validatePRCorpusLumpsum(finalValue)
              }));
            }
          } else {
            // Still update the field but don't calculate annuity
            setFormData(prev => ({
              ...prev,
              [name]: finalValue,
              PRCorpusLumpsum: finalValue, // Sync with PRCorpusLumpsum
              annuityPercentage: ''
            }));
            // Validate both fields
            setErrors(prev => ({
              ...prev,
              [name]: validateWithdrawalPR(finalValue),
              PRCorpusLumpsum: validatePRCorpusLumpsum(finalValue)
            }));
          }
          return;
        }
      }
    }

    // Handle NPSCorpus - format with commas for display, store numeric value
    if (name === 'NPSCorpus') {
      if (value === '' || value === null) {
        finalValue = '';
      } else {
        // Remove all non-digit characters (except decimal point if needed)
        const numericValue = value.replace(/[^\d]/g, '');
        if (numericValue === '') {
          finalValue = '';
        } else {
          // Format with commas for display
          finalValue = formatNumberWithCommas(numericValue);
        }
      }
      // Store the numeric value (without commas) in state for calculations
      const numericValueForState = removeCommas(finalValue);
      setFormData(prev => ({
        ...prev,
        [name]: numericValueForState // Store without commas for calculations
      }));

      // Validate using numeric value (without commas)
      const error = validateNPSCorpus(numericValueForState);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
      return; // Return early to prevent default handling
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : finalValue
    }));

    // Validate based on field name
    let error = null;
    if (name !== 'PRCorpusLumpsum' && name !== 'NPSCorpus') {
      switch (name) {
        case 'WithdrawalPR':
          error = validateWithdrawalPR(finalValue);
          break;
        case 'WithdrawalAmount':
          error = validateWithdrawalAmount(value);
          break;
        case 'AnnuityValue':
          error = validateAnnuityValue(value);
          break;
        case 'WorkSectorDOB':
          // Convert DD-MM-YYYY (internal format) to MM-DD-YYYY for validation
          const dateForValidation = convertDDMMYYYYToMMDDYYYY(value) || value;
          error = validateWorkSectorDOB(dateForValidation);
          break;
        default:
          break;
      }
    } else {
      // Validate PRCorpusLumpsum
      if (finalValue === '') {
        error = validatePRCorpusLumpsum('');
      } else {
        error = validatePRCorpusLumpsum(finalValue);
      }
    }

    // Update errors
    setErrors(prev => ({
      ...prev,
      [name]: error
    }));

    // Calculate age from date of birth
    if (name === 'WorkSectorDOB' && value) {
      // Convert DD-MM-YYYY to YYYY-MM-DD for calculateAge
      const dateForCalculation = convertDDMMYYYYToYYYYMMDD(value) || value;
      const age = calculateAge(dateForCalculation);
      setCurrentAge(age);
    }
  };

  const handleDateChange = (e) => {
    const dateValue = e.target.value; // Format: MM-DD-YYYY from CustomDatePicker

    if (dateValue) {
      // Convert MM-DD-YYYY to DD-MM-YYYY for internal storage
      const formattedDate = convertMMDDYYYYToDDMMYYYY(dateValue);

      setFormData(prev => ({
        ...prev,
        WorkSectorDOB: formattedDate
      }));

      // Validate date (use MM-DD-YYYY format for validation)
      const error = validateWorkSectorDOB(dateValue);
      setErrors(prev => ({
        ...prev,
        WorkSectorDOB: error
      }));

      // Calculate age (convert to YYYY-MM-DD for calculateAge)
      const dateForAge = convertDDMMYYYYToYYYYMMDD(formattedDate);
      const age = calculateAge(dateForAge);
      setCurrentAge(age);
    } else {
      setFormData(prev => ({
        ...prev,
        WorkSectorDOB: ''
      }));
      setErrors(prev => ({
        ...prev,
        WorkSectorDOB: validateWorkSectorDOB('')
      }));
      setCurrentAge(0);
    }
  };

  const handleSelectChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updatedData = {
        ...prev,
        [name]: value
      };

      // Validate dropdown fields
      let error = null;
      switch (name) {
        case 'WorkSectorName':
          error = validateRequiredField(value, 'Work Sector');
          break;
        case 'AnnuitySchemeName':
          error = validateRequiredField(value, 'Annuity Scheme');
          break;
        case 'AnnuityFrequencySchemeName':
          error = validateRequiredField(value, 'Annuity Frequency');
          break;
        default:
          break;
      }

      // Update errors
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));

      // Pass data to parent component
      if (onDataChange) {
        // Get selected bank - use null if no bank is selected
        const selectedBank = selectedBanks.length > 0
          ? bankList.find(bank => selectedBanks.includes(bank.BankMasterID)) || null
          : null;

        // Get bank name from selected bank
        const bankName = selectedBank ? (selectedBank.BankName || selectedBank.Name || '') : '';

        onDataChange({
          selectedBank: selectedBank,
          AnnuityServiceProviderName: bankName,
          annuityScheme: updatedData.AnnuitySchemeName,
          annuityFrequency: updatedData.AnnuityFrequencySchemeName
        });
      }

      return updatedData;
    });
  };

  const handleBankCheckboxChange = (bankId) => {
    setSelectedBanks(prev => {
      const isCurrentlySelected = prev.includes(bankId);
      const newSelected = isCurrentlySelected
        ? prev.filter(id => id !== bankId) // Remove if already selected
        : [bankId]; // Only allow single selection

      // Pass selected bank data to parent
      if (onDataChange) {
        if (isCurrentlySelected) {
          // Bank is being unselected - pass null
          onDataChange({
            selectedBank: null,
            AnnuityServiceProviderName: '',
            annuityScheme: formData.AnnuitySchemeName,
            annuityFrequency: formData.AnnuityFrequencySchemeName
          });
        } else {
          // Bank is being selected - pass bank data
          const selectedBank = bankList.find(bank => bank.BankMasterID === bankId);
          const bankName = selectedBank ? (selectedBank.BankName || selectedBank.Name || '') : '';
          onDataChange({
            selectedBank: selectedBank || null,
            AnnuityServiceProviderName: bankName,
            annuityScheme: formData.AnnuitySchemeName,
            annuityFrequency: formData.AnnuityFrequencySchemeName
          });
        }
      }

      return newSelected;
    });
  };

  return (
    <div className="section-container">
      <div className="card shadow-sm">
        <div className="card-body p-4">
          <form>
            <div className="row g-3">
              {/* Normal Withdrawal Question */}
              <div className="col-12">
                <label className="form-label d-block mb-3">
                  Would you like to have normal withdrawal (Lump sum & Annuity withdrawal)
                </label>
                <div className="d-flex gap-4">
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="normalWithdrawal"
                      id="withdrawalYes"
                      value="yes"
                      checked={formData.normalWithdrawal === 'yes'}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="withdrawalYes">
                      Yes
                    </label>
                  </div>
                  <div className="form-check">
                    <input
                      className="form-check-input"
                      type="radio"
                      name="normalWithdrawal"
                      id="withdrawalNo"
                      value="no"
                      checked={formData.normalWithdrawal === 'no'}
                      onChange={handleInputChange}
                    />
                    <label className="form-check-label" htmlFor="withdrawalNo">
                      No
                    </label>
                  </div>
                </div>
              </div>

              {/* Lump Sum Withdrawal Percentage */}
              <div className="col-md-6">
                <label htmlFor="PRCorpusLumpsum" className="form-label">
                  Percent of corpus opted for lump sum withdrawal (Max 20%)
                </label>
                <input
                  type="number"
                  className={`form-control ${errors.PRCorpusLumpsum ? 'is-invalid' : ''}`}
                  id="PRCorpusLumpsum"
                  name="PRCorpusLumpsum"
                  value={formData.PRCorpusLumpsum}
                  onChange={handleInputChange}
                  onWheel={(e) => e.target.blur()}
                  min="0"
                  max="20"
                  step="0.01"
                  placeholder="Enter percentage (max 20%)"
                  style={{ textTransform: 'uppercase' }}
                />
                {errors.PRCorpusLumpsum && (
                  <div className="invalid-feedback d-block">
                    {errors.PRCorpusLumpsum}
                  </div>
                )}
              </div>

              {/* Annuity Purchase Percentage */}
              <div className="col-md-6">
                <label htmlFor="annuityPercentage" className="form-label">
                  Percent of corpus opted for purchase of annuity (Min 80%)
                </label>
                <input
                  type="number"
                  className="form-control"
                  id="annuityPercentage"
                  name="annuityPercentage"
                  value={formData.annuityPercentage}
                  onChange={handleInputChange}
                  min="0"
                  max="80"
                  placeholder="Enter percentage (Min 80%)"
                  readOnly
                  style={{ textTransform: 'uppercase' }}
                />
              </div>

              {/* PensionPlus Calculator Section */}
              <div className="col-12 mt-4 mb-3">
                <div className="card-header" style={{
                  background: 'linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)',
                  color: 'white',
                  padding: '1rem 1.5rem',
                  borderRadius: '8px 8px 0 0',
                  border: 'none'
                }}>
                  <h5 className="mb-0" style={{ fontSize: '1.1rem', fontWeight: '600', display: 'flex', alignItems: 'center' }}>
                    <i className="fas fa-calculator me-2"></i>
                    PensionPlus Calculator
                  </h5>
                </div>
              </div>

              {/* Work Sector */}
              <div className="col-md-6">
                <label htmlFor="WorkSectorName" className="form-label">
                  Work Sector
                </label>
                <CustomDropdown
                  options={workSectorList}
                  value={formData.WorkSectorName}
                  onChange={handleSelectChange}
                  placeholder="Select work sector..."
                  name="WorkSectorName"
                />
                {errors.WorkSectorName && (
                  <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {errors.WorkSectorName}
                  </div>
                )}
              </div>

              {/* Subscriber's Date of Birth */}
              <div className="col-md-6">
                <label htmlFor="WorkSectorDOB" className="form-label">
                  Subscriber's Date of Birth
                </label>
                <CustomDatePicker
                  name="WorkSectorDOB"
                  value={convertDDMMYYYYToMMDDYYYY(formData.WorkSectorDOB)}
                  onChange={handleDateChange}
                  maxDate={new Date().toISOString().split('T')[0]}
                  className={errors.WorkSectorDOB ? 'is-invalid' : ''}
                  placeholder="MM-DD-YYYY"
                />
                {currentAge > 0 && (
                  <div className="mt-2" style={{ fontSize: '0.875rem', color: '#000000', fontWeight: '500' }}>
                    <i className="fas fa-info-circle me-2" style={{ color: '#0d6efd' }}></i>
                    Your current age : {currentAge} years
                  </div>
                )}
                {errors.WorkSectorDOB && (
                  <div className="invalid-feedback d-block">
                    {errors.WorkSectorDOB}
                  </div>
                )}
              </div>


              {/* NPS Corpus */}
              <div className="col-md-6">
                <label htmlFor="NPSCorpus" className="form-label">
                  NPS Corpus
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.NPSCorpus ? 'is-invalid' : ''}`}
                  id="NPSCorpus"
                  name="NPSCorpus"
                  value={formData.NPSCorpus ? formatNumberWithCommas(formData.NPSCorpus) : ''}
                  onChange={handleInputChange}
                  placeholder="Enter NPS corpus amount (e.g., 10,000)"
                  style={{ textTransform: 'uppercase' }}
                />
                {errors.NPSCorpus && (
                  <div className="invalid-feedback d-block">
                    {errors.NPSCorpus}
                  </div>
                )}
              </div>

              {/* Withdrawal Percentage */}
              <div className="col-md-6">
                <label htmlFor="WithdrawalPR" className="form-label">
                  Withdrawal Percentage <span className="text-danger">*</span>
                </label>
                <input
                  type="number"
                  className={`form-control ${errors.WithdrawalPR ? 'is-invalid' : ''}`}
                  id="WithdrawalPR"
                  name="WithdrawalPR"
                  value={formData.WithdrawalPR}
                  onChange={handleInputChange}
                  onWheel={(e) => e.target.blur()}
                  placeholder="Enter withdrawal percentage (max 20%)"
                  min="0"
                  max="20"
                  step="0.01"
                  style={{ textTransform: 'uppercase' }}
                />
                {errors.WithdrawalPR && (
                  <div className="invalid-feedback d-block">
                    {errors.WithdrawalPR}
                  </div>
                )}
              </div>


              {/* Withdrawal Amount */}
              <div className="col-md-6">
                <label htmlFor="WithdrawalAmount" className="form-label">
                  Withdrawal Amount
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.WithdrawalAmount ? 'is-invalid' : ''}`}
                  id="WithdrawalAmount"
                  name="WithdrawalAmount"
                  value={formData.WithdrawalAmount ? formatIndianNumber(parseFloat(formData.WithdrawalAmount)) : ''}
                  onChange={handleInputChange}
                  placeholder="Withdrawal amount"
                  readOnly
                  style={{ textTransform: 'uppercase' }}
                />
                {errors.WithdrawalAmount && (
                  <div className="invalid-feedback d-block">
                    {errors.WithdrawalAmount}
                  </div>
                )}
              </div>


              {/* Annuity Value */}
              <div className="col-md-6">
                <label htmlFor="AnnuityValue" className="form-label">
                  Annuity Value
                </label>
                <input
                  type="text"
                  className={`form-control ${errors.AnnuityValue ? 'is-invalid' : ''}`}
                  id="AnnuityValue"
                  name="AnnuityValue"
                  value={formData.AnnuityValue ? formatIndianNumber(parseFloat(formData.AnnuityValue)) : ''}
                  onChange={handleInputChange}
                  placeholder="Annuity value"
                  readOnly
                  style={{ textTransform: 'uppercase' }}
                />
                {errors.AnnuityValue && (
                  <div className="invalid-feedback d-block">
                    {errors.AnnuityValue}
                  </div>
                )}
              </div>


              {/* Select Annuity Scheme */}
              <div className="col-md-6">
                <label htmlFor="AnnuitySchemeName" className="form-label">
                  Select Annuity Scheme
                </label>
                <AnnuitySchemeDropdown
                  options={annuitySchemeList}
                  value={formData.AnnuitySchemeName}
                  onChange={handleSelectChange}
                  placeholder="Select annuity scheme..."
                  name="AnnuitySchemeName"
                />
                {errors.AnnuitySchemeName && (
                  <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {errors.AnnuitySchemeName}
                  </div>
                )}
              </div>

              {/* Select Annuity Frequency */}
              <div className="col-md-6">
                <label htmlFor="AnnuityFrequencySchemeName" className="form-label">
                  Select Annuity Frequency
                </label>
                <CustomDropdown
                  options={annuityFrequencyList}
                  value={formData.AnnuityFrequencySchemeName}
                  onChange={handleSelectChange}
                  placeholder="Select annuity frequency..."
                  name="AnnuityFrequencySchemeName"
                />
                {errors.AnnuityFrequencySchemeName && (
                  <div className="text-danger" style={{ fontSize: '0.875rem', marginTop: '0.25rem' }}>
                    {errors.AnnuityFrequencySchemeName}
                  </div>
                )}
              </div>

            </div>
          </form>
        </div>
      </div>

      {/* Bank Pension List Section */}
      <div className="card shadow-sm mt-5">
        <div className="card-body p-3">
          {loadingBanks ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading bank list...</p>
            </div>
          ) : Array.isArray(bankList) && bankList.length > 0 ? (
            <div className='mt-3'>
              <h6 className="mb-4" style={{ fontSize: '1.1rem', fontWeight: '600', color: '#000' }}>
                Select Annuity Service Providers
              </h6>
              <div className="row g-3">
                {bankList.map((bank, index) => {
                  const bankId = bank.BankMasterID || bank.BankID || index;
                  const bankName = bank.BankName || bank.Name || 'Unknown Bank';
                  const bankLogo = bank.BankLogo || bank.Logo || '';
                  const pensionData = bank.BankPensionData || bank.PensionData || [];
                  const amount = pensionData[0]?.Amount || pensionData?.Amount || '0.00';
                  const percentage = pensionData[0]?.PensionPer || pensionData?.PensionPer || pensionData[0]?.Percentage || '0.0';
                  const isSelected = selectedBanks.includes(bankId);

                  return (
                    <div key={bankId} className="col-lg-4 col-md-6 col-sm-12">
                      <div
                        className="rounded"
                        style={{
                          backgroundColor: '#e7f1ff',
                          padding: '0.75rem 1rem',
                          borderRadius: '10px',
                          transition: 'all 0.3s ease',
                          cursor: 'pointer',
                          border: isSelected ? '2px solid #0d6efd' : '1px solid #cfe2ff',
                          boxShadow: isSelected ? '0 4px 8px rgba(13, 110, 253, 0.2)' : '0 2px 4px rgba(0,0,0,0.1)'
                        }}
                        onClick={() => handleBankCheckboxChange(bankId)}
                      >
                        <div className="d-flex align-items-center justify-content-between">
                          {/* Left: Logo and Bank Name */}
                          <div className="d-flex align-items-center" style={{ flex: 1, minWidth: 0 }}>
                            {bankLogo && (
                              <img
                                src={bankLogo.startsWith('http') ? bankLogo : `${IMAGE_URL}${bankLogo}`}
                                alt={bankName}
                                style={{
                                  width: '90px',
                                  height: '90px',
                                  objectFit: 'contain',
                                  marginRight: '20px',
                                  borderRadius: '6px',
                                  flexShrink: 0
                                }}
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            )}
                            <div style={{ minWidth: 0, flex: 1 }}>
                              <div style={{
                                fontSize: '0.875rem',
                                fontWeight: '600',
                                color: '#333',
                                marginBottom: '0',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }}>
                                {bankName}
                              </div>
                            </div>
                          </div>

                          {/* Middle: Selection Indicator (Checkmark Icon) */}
                          <div className="mx-2" style={{ flexShrink: 0 }}>
                            <div
                              style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: '6px',
                                backgroundColor: isSelected ? '#0d6efd' : 'transparent',
                                border: isSelected ? 'none' : '2px solid #dee2e6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              {isSelected && (
                                <i
                                  className="fas fa-check"
                                  style={{
                                    color: 'white',
                                    fontSize: '0.75rem',
                                    fontWeight: 'bold'
                                  }}
                                ></i>
                              )}
                            </div>
                          </div>

                          {/* Right: Financial Details */}
                          <div className="text-end" style={{ minWidth: '90px', flexShrink: 0 }}>
                            <div style={{
                              fontSize: '0.875rem',
                              color: '#000',
                              fontWeight: '600',
                              marginBottom: '2px',
                              lineHeight: '1.2'
                            }}>
                              ₹ {formatNumber(amount)}
                            </div>
                            <div style={{
                              fontSize: '0.95rem',
                              color: '#28a745',
                              fontWeight: '700',
                              lineHeight: '1.2'
                            }}>
                              {formatNumber(percentage)}%
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-muted mb-0">
                {formData.AnnuityValue && formData.AnnuityFrequencySchemeName && formData.AnnuitySchemeName && currentAge
                  ? 'No banks available for the selected criteria'
                  : 'Please fill all required fields to see bank list'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SectionC;

